// packages/web/src/components/assessment/PhaseBar.tsx
import { useNavigate } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AssessmentWorkflowState } from '@app/types';

// ANSWERS_SUBMITTED is a transitional state — analysis is running
const PROCESSING_STATES = new Set<AssessmentWorkflowState>([
  AssessmentWorkflowState.ANSWERS_SUBMITTED,
]);

interface PhaseBarProps {
  currentState: AssessmentWorkflowState;
  assessmentId: string;
}

const stateToStep: Record<AssessmentWorkflowState, number> = {
  [AssessmentWorkflowState.COLLECTING_EVIDENCE]: 1,
  [AssessmentWorkflowState.ANSWERING_QUESTIONS]: 2,
  [AssessmentWorkflowState.ANSWERS_SUBMITTED]: 3,
  [AssessmentWorkflowState.REVIEWING_REPORT]: 3,
  [AssessmentWorkflowState.SHARED]: 3,
  [AssessmentWorkflowState.FINALIZED]: 3,
};

const PHASES = [
  {
    step: 1,
    label: 'Collect Evidence',
    state: AssessmentWorkflowState.COLLECTING_EVIDENCE,
    route: '',
  },
  {
    step: 2,
    label: 'Answer Questions',
    state: AssessmentWorkflowState.ANSWERING_QUESTIONS,
    route: 'questions',
  },
  {
    step: 3,
    label: 'Review Report',
    state: AssessmentWorkflowState.REVIEWING_REPORT,
    route: 'analysis',
  },
] as const;

/**
 * Horizontal 3-step progress indicator for assessment phases.
 * Shows which phase (evidence, questions, report) is current,
 * allows navigation back to completed phases, and displays a
 * processing indicator during transitional states.
 *
 * @param currentState - The assessment's current workflow state
 * @param assessmentId - The assessment ID, used for navigation routes
 */
export function PhaseBar({ currentState, assessmentId }: PhaseBarProps): JSX.Element {
  const navigate = useNavigate();
  const currentStep = stateToStep[currentState];
  const isProcessing = PROCESSING_STATES.has(currentState);

  return (
    <nav
      className="flex items-center justify-center gap-0 px-6 py-2 bg-background border-b border-border"
      aria-label="Assessment progress"
    >
      <ol className="flex items-center gap-0 list-none p-0 m-0">
        {PHASES.map((phase, index) => {
          const isCompleted = phase.step < currentStep;
          const isCurrent = phase.step === currentStep;
          const isFuture = phase.step > currentStep;

          return (
            <li
              key={phase.step}
              className="flex items-center"
              aria-current={isCurrent ? 'step' : undefined}
            >
              {/* Connector line before (skip first) */}
              {index > 0 && (
                <div
                  className={cn(
                    'w-10 h-px mx-1',
                    isCompleted || isCurrent ? 'bg-green-500' : 'bg-border'
                  )}
                />
              )}

              {/* Step button */}
              <button
                type="button"
                onClick={() => {
                  if (isCompleted) {
                    const route = phase.route
                      ? `/assessments/${assessmentId}/${phase.route}`
                      : `/assessments/${assessmentId}`;
                    navigate(route);
                  }
                }}
                disabled={!isCompleted}
                data-testid={
                  isCompleted
                    ? `phase-step-${phase.step}-complete`
                    : isCurrent && isProcessing
                      ? `phase-step-${phase.step}-processing`
                      : undefined
                }
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                  isCompleted &&
                    'text-green-700 cursor-pointer hover:bg-green-50',
                  isCurrent && 'text-primary bg-primary/10',
                  isFuture && 'text-muted-foreground cursor-default'
                )}
              >
                {/* Step indicator */}
                <span
                  className={cn(
                    'flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold',
                    isCompleted && 'bg-green-600 text-white',
                    isCurrent && 'bg-primary text-primary-foreground',
                    isFuture && 'bg-muted text-muted-foreground'
                  )}
                >
                  {isCompleted ? (
                    <Check className="w-3 h-3" aria-hidden="true" />
                  ) : isCurrent && isProcessing ? (
                    <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
                  ) : (
                    phase.step
                  )}
                </span>

                {/* Label */}
                <span className="hidden sm:inline">{phase.label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
